import clsx from 'clsx';
import { GlobalStyles, List, styled } from '@mui/material';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import BusinessIcon from '@mui/icons-material/Business';
import NavGroup from './NavGroup';
import NavLink from './NavItem';
import { useMeCore } from '../../core/modules/me';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { useCompanyCore } from '../../core/modules/company';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import CompareArrowsOutlinedIcon from '@mui/icons-material/CompareArrowsOutlined';
interface Props {
  className?: string;
}

const inputGlobalStyles = (
  <GlobalStyles
    styles={() => ({
      '.popper-navigation-list': {
        '& .fuse-list-item': {
          padding: '8px 12px 8px 12px',
          height: 40,
          minHeight: 40,
          '& .fuse-list-item-text': {
            padding: '0 0 0 8px',
          },
        },
        '&.dense': {
          '& .fuse-list-item': {
            minHeight: 32,
            height: 32,
            '& .fuse-list-item-text': {
              padding: '0 0 0 8px',
            },
          },
        },
      },
    })}
  />
);

const StyledList = styled(List)(({ theme }) => ({
  '& .fuse-list-item': {
    '&:hover': {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.05)'
          : 'rgba(0,0,0,.04)',
    },
    '&:focus:not(.active)': {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.06)'
          : 'rgba(0,0,0,.05)',
    },
    padding: '8px 12px 8px 12px',
    height: 40,
    minHeight: 40,
    '&.level-0': {
      minHeight: 44,
      minminHeight: 44,
    },
    '& .fuse-list-item-text': {
      padding: '0 0 0 8px',
    },
  },
  '&.active-square-list': {
    '& .fuse-list-item': {
      borderRadius: '0',
    },
  },
}));

const Navigation = (props: Props) => {
  const { t } = useTranslation();

  const { className } = props;
  const { user } = useMeCore();
  const { company } = useCompanyCore();
  const showAdminDashboard = useMemo(() => user?.is_superuser, [user?.is_superuser]);
  const showCompanyAdmin = useMemo(() => user?.is_superuser, [user?.is_superuser]);
  return (
    <>
      {inputGlobalStyles}
      <StyledList
        className={clsx(
          'navigation whitespace-nowrap items-center flex flex-col px-12',
          className
        )}
      >
        <NavLink
          title="Mis compañías"
          url="/dashboard/me/companies"
          icon={<AssignmentTurnedInOutlinedIcon />}
        />
        {company && (
          <NavGroup title={company.name} subtitle="Panel de la compañia">
            <NavLink
              title="Alertas de Stock"
              url={`/dashboard/metrics/${company.id}`}
              icon={<NotificationsActiveOutlinedIcon />}
            />
            <NavLink
              title="Consultor de Stock"
              url={`/dashboard/stock-consultant/${company.id}`}
              icon={<SearchOutlinedIcon />}
            />
            <NavLink
              title="Métricas"
              url={`/dashboard/reports/${company.id}`}
              icon={<InsightsOutlinedIcon />}
            />
            {(user?.role == 'Administrador de empresa' || showCompanyAdmin) && (
              <NavLink
                title="Usuarios"
                url={`/dashboard/manage-users/${company.id}`}
                icon={<GroupOutlinedIcon />}
              />
            )}
            {(user?.role == 'Administrador de empresa' || showCompanyAdmin) && (
              <NavLink
                title="Importar"
                url={`/dashboard/import/${company.id}`}
                icon={<CloudUploadOutlinedIcon />}
              />
            )}
            {(user?.role == 'Administrador de empresa' || showCompanyAdmin) && company.inventory_count_enabled && (
              <NavLink
                title="Toma de Inventario"
                url={`/dashboard/inventory-count/${company.id}`}
                icon={<FactCheckOutlinedIcon />}
              />
            )}
            {/* Visible para Usuario, Administrador de empresa y superadmin --
                a diferencia de los demas links de este archivo, Stock
                Comparison necesita que un Usuario base tambien pueda entrar
                (puede subir el archivo, aunque no configurar el umbral) --
                pedido explicito de Seba. Tomador de Inventario queda afuera
                explicitamente: su UI es CountingLayout, un arbol aparte, y
                esto es una tarea de back-office. */}
            {user?.role !== 'Tomador de Inventario' && company.stock_comparison_enabled && (
              <NavLink
                title="Stock Comparison"
                url={`/dashboard/stock-comparison/${company.id}`}
                icon={<CompareArrowsOutlinedIcon />}
              />
            )}
            {/* Google Sheets: vive dentro de la pestaña 'Integraciones' de
                Configuración -- pedido explicito de Seba, no como link propio
                del menu. Ver CompanySettings.tsx. */}
            {(user?.role == 'Administrador de empresa' || showCompanyAdmin) && (
              <NavLink
                title="Configuración"
                url={`/dashboard/settings/${company.id}`}
                icon={<SettingsOutlinedIcon />}
              />
            )}
          </NavGroup>
        )}
        {showAdminDashboard && (
          <NavGroup title={t('sidebar.menu.admin.title')} subtitle={t('sidebar.menu.admin.subtitle')}>
            <NavLink
              title={t('sidebar.link.users')}
              url="/dashboard/users"
              icon={<GroupOutlinedIcon />}
            />
            <NavLink
              title="Compañías"
              url="/dashboard/companies"
              icon={<BusinessIcon />}
            /> 
          </NavGroup>
        )}
      </StyledList>
    </>
  );
};

export default Navigation;
